import HomeIcon from "@mui/icons-material/Home";
import usePageLanguage from "../hooks/usePageLanguage";
import { useAppSelector } from "../../../store/hooks";
import React, { useEffect, useMemo, useState } from "react";
import { Product } from "../interfaces/catalogProduct";
import {
  buildProductBreadcrumbCategoryHref,
  resolveProductBreadcrumbHomeHref,
  resolveProductBreadcrumbProductsHref,
} from "../utilities/productBreadcrumbLinks";

type CategoryNameTranslation = {
  name?: string;
};

type CategoryTranslations = Record<
  string,
  CategoryNameTranslation | string | undefined
>;

type CategoryNode = {
  uuid: string;
  parentUuid: string | null;
  translations?: CategoryTranslations;
  fallbackName?: string;
  sortOrder?: number;
};

type ProductBreadcrumbsProps = {
  catalogProduct?: Product;
};

declare const API: unknown;

function pickCategoryName(
  category: CategoryNode,
  locale: string
): string | undefined {
  const preferred = category.translations?.[locale];
  if (typeof preferred === "string" && preferred.trim()) {
    return preferred;
  }
  if (
    preferred &&
    typeof preferred === "object" &&
    typeof preferred.name === "string" &&
    preferred.name.trim()
  ) {
    return preferred.name;
  }

  const fallbackLocales = ["et", "en", "ru"];
  for (const fallbackLocale of fallbackLocales) {
    const translation = category.translations?.[fallbackLocale];
    if (typeof translation === "string" && translation.trim()) {
      return translation;
    }
    if (
      translation &&
      typeof translation === "object" &&
      typeof translation.name === "string" &&
      translation.name.trim()
    ) {
      return translation.name;
    }
  }

  return category.fallbackName;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

function toCategoryNode(rawCategory: unknown): CategoryNode | null {
  const record = asRecord(rawCategory);
  if (!record) {
    return null;
  }

  const uuid = typeof record.uuid === "string" ? record.uuid : null;
  if (!uuid) {
    return null;
  }

  const parentUuid =
    typeof record.parentUuid === "string"
      ? record.parentUuid
      : typeof record.parent_uuid === "string"
        ? record.parent_uuid
        : null;

  const translations = record.translations as
    | CategoryTranslations
    | undefined;

  const fallbackName = typeof record.name === "string" ? record.name : undefined;

  const sortOrder =
    typeof record.sortOrder === "number"
      ? record.sortOrder
      : typeof record.sort_order === "number"
        ? record.sort_order
        : undefined;

  return {
    uuid,
    parentUuid,
    translations,
    fallbackName,
    sortOrder,
  };
}

export default function ProductBreadcrumbs({
  catalogProduct,
}: ProductBreadcrumbsProps) {
  const lang = usePageLanguage();
  const pages = useAppSelector((state) => state.builder.pages.list);
  const languages = useAppSelector((state) => state.builder.languageSettings.activeLanguages);
  const [allCategories, setAllCategories] = useState<CategoryNode[]>([]);
  const homeHref = resolveProductBreadcrumbHomeHref(pages, lang, languages);
  const languageProductsHref = resolveProductBreadcrumbProductsHref(
    pages,
    lang,
    languages
  );

  useEffect(() => {
    const loadCategories = () => {
      if (typeof API !== "undefined") {
        // @ts-ignore
        return window.axios("/productCategories.json", {
          method: "GET",
          cache: "no-cache",
          headers: { "Content-Type": "application/json" },
        }).then((res: { data: unknown }) => {
          const normalized = Array.isArray(res.data)
            ? res.data.map(toCategoryNode).filter((node): node is CategoryNode => node != null)
            : [];
          setAllCategories(normalized);
        });
      }

      return fetch("/webstoreApi/productCategories.json", {
        method: "GET",
        cache: "no-cache",
        headers: { "Content-Type": "application/json" },
      })
        .then((res) => res.json())
        .then((data: unknown) => {
          const normalized = Array.isArray(data)
            ? data.map(toCategoryNode).filter((node): node is CategoryNode => node != null)
            : [];
          setAllCategories(normalized);
        });
    };

    loadCategories().catch(() => {
      setAllCategories([]);
    });
  }, []);

  const categoryUuids = useMemo(() => {
    if (!catalogProduct?.productCategories?.length) {
      return [];
    }

    return catalogProduct.productCategories
      .map((category) => category.uuid)
      .filter((uuid): uuid is string => typeof uuid === "string" && uuid.length > 0);
  }, [catalogProduct?.productCategories]);

  const breadcrumbChains = useMemo(() => {
    if (!categoryUuids.length || !allCategories.length) {
      return [] as CategoryNode[][];
    }

    const byUuid = new Map<string, CategoryNode>();
    allCategories.forEach((category) => {
      byUuid.set(category.uuid, category);
    });

    const chainKeySet = new Set<string>();
    const chains = categoryUuids
      .map((categoryUuid) => {
        const chain: CategoryNode[] = [];
        let current = byUuid.get(categoryUuid);
        let guard = 0;

        while (current && guard < 20) {
          chain.unshift(current);
          current = current.parentUuid ? byUuid.get(current.parentUuid) : undefined;
          guard += 1;
        }

        return chain;
      })
      .filter((chain) => chain.length > 0)
      .filter((chain) => {
        const key = chain.map((category) => category.uuid).join(">");
        if (chainKeySet.has(key)) {
          return false;
        }
        chainKeySet.add(key);
        return true;
      });

    return chains.sort((a, b) => {
      const aLastSort = a[a.length - 1]?.sortOrder ?? 0;
      const bLastSort = b[b.length - 1]?.sortOrder ?? 0;
      return aLastSort - bLastSort;
    });
  }, [allCategories, categoryUuids]);

  return (
    <div className="container">
      <div className="page-header page-detail-page-header">
        <div className="product-detail-page-nav">
          <a className="breadcrumb icon" href={homeHref} role="button">
            <HomeIcon />{" "}
          </a>
          <b className="web-category-link-sepparator">/</b>
          {breadcrumbChains.length > 0
            ? breadcrumbChains.map((chain, chainIndex) => (
                <React.Fragment key={chain.map((category) => category.uuid).join(">")}>
                  {chainIndex > 0 ? " , " : null}
                  {chain.map((category, categoryIndex) => (
                    <React.Fragment key={category.uuid}>
                      {categoryIndex > 0 ? (
                        <b className="web-category-link-sepparator">/</b>
                      ) : null}
                      <a
                        className="breadcrumb"
                        href={buildProductBreadcrumbCategoryHref(
                          languageProductsHref,
                          category.uuid
                        )}
                        role="button"
                      >
                        {pickCategoryName(category, lang) ?? category.uuid}
                      </a>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))
            : null}
        </div>
      </div>
    </div>
  );
}
